import { API_ROOT } from '../../config';
import { getNetwork } from '../../utils/getNetwork';

/* =====================================================================
 * analyticsIndicatorsApi.js — работа с реальным API (без мок-данных).
 *
 * КОНТРАКТ БЭКЕНДА (по факту ответов):
 *   /values → { total, page, size, items: [...] }   ← список под "items"
 *   запись:  id(uuid), indicator_id, indicator:{ru,kk}, measure_unit:{ru,kk},
 *            period_type, period_type_name:{ru,kk}, period_start/end (ISO T00:00),
 *            period_label: "строка" (одноязычная!), value, creator: "login".
 *   /dictionaries → indicator.name:{ru,kk}, measure_unit_id (id-ссылка).
 *
 * tr() понимает: nested {ru,kk} | одноязычную строку | плоские *_ru/*_kk | camelCase.
 * Без ?? и ?. (react-app-rewired их не транспилирует).
 *
 * ВНИМАНИЕ: в getDictionaries оставлен ВРЕМЕННЫЙ console.log [analytics] — удалить.
 * ===================================================================== */

const BASE = `${API_ROOT}/api/analytics`;

const toCamel = (s) => s.replace(/_([a-z])/g, (_, c) => c.toUpperCase());

const pick = (o, key) => {
  if (o === null || o === undefined) return undefined;
  return o[key] !== undefined ? o[key] : o[toCamel(key)];
};

const def = (v, d) => (v === null || v === undefined ? d : v);

// nested {ru,kk} → строка; одноязычная строка → как есть; иначе плоские *_ru/*_kk.
const tr = (o, base, lang) => {
  if (o === null || o === undefined) return undefined;
  const nested = pick(o, base);
  if (nested !== null && typeof nested === 'object') return nested[lang];
  if (typeof nested === 'string') return nested;
  return pick(o, `${base}_${lang}`);
};

function mapIndicator(it, unitById) {
  const uid = pick(it, 'measure_unit_id');
  const uObj = uid != null ? unitById[uid] : null;
  return {
    id: pick(it, 'id'),
    name_ru: tr(it, 'name', 'ru'),
    name_kk: tr(it, 'name', 'kk'),
    sector_ru: def(tr(it, 'sector', 'ru'), null),
    sector_kk: def(tr(it, 'sector', 'kk'), null),
    // единица: сперва из inline-объекта measure_unit, затем из справочника единиц по id
    unit_ru: def(tr(it, 'measure_unit', 'ru'), uObj ? tr(uObj, 'name', 'ru') : undefined),
    unit_kk: def(tr(it, 'measure_unit', 'kk'), uObj ? tr(uObj, 'name', 'kk') : undefined),
    allowed_period_types: pick(it, 'allowed_period_types') || [],
    default_period_type: pick(it, 'default_period_type'),
    accum: pick(it, 'accum'),
    period_start_rule: pick(it, 'period_start_rule'),
    label_style: pick(it, 'label_style'),
    allow_negative: pick(it, 'allow_negative') || false,
    sort_order: pick(it, 'sort_order'),
  };
}

function mapPeriodType(p) {
  return {
    key: pick(p, 'key'),
    pid: pick(p, 'pid'),
    name_ru: tr(p, 'name', 'ru'),
    name_kk: tr(p, 'name', 'kk'),
  };
}

// Запись значения → плоский контракт. Имя/единица берутся прямо с записи (inline).
function mapRow(r) {
  return {
    id: pick(r, 'id'),
    indicator_id: pick(r, 'indicator_id'),
    name_ru: tr(r, 'indicator', 'ru'),
    name_kk: tr(r, 'indicator', 'kk'),
    unit_ru: tr(r, 'measure_unit', 'ru'),
    unit_kk: tr(r, 'measure_unit', 'kk'),
    period_type: pick(r, 'period_type'),
    period_year: pick(r, 'period_year'),
    period_index: pick(r, 'period_index'),
    value: pick(r, 'value'),
    period_start: pick(r, 'period_start'),
    period_end: pick(r, 'period_end'),
    period_label_ru: tr(r, 'period_label', 'ru'),
    period_label_kk: tr(r, 'period_label', 'kk'),
    created_at: def(pick(r, 'created_at'), pick(r, 'created_date')),
    created_by_ru: def(tr(r, 'created_by', 'ru'), pick(r, 'creator')),
    created_by_kk: def(tr(r, 'created_by', 'kk'), pick(r, 'creator')),
  };
}

const FIELD_MAP = {
  indicator_id: 'indicator',
  period_type: 'ptype',
  period_year: 'period',
  period_index: 'period',
  value: 'value',
};

export function normalizeApiError(error) {
  const status = error && error.response ? error.response.status : 0;
  const data = error && error.response ? error.response.data : null;

  if (status === 403)
    return { status, forbidden: true, fields: [], messageKey: 'analyticsIndicators.err.forbidden' };

  if (status === 409) {
    return {
      status,
      fields: [{ field: 'period', messageKey: 'analyticsIndicators.err.dup' }],
      messageKey: null,
    };
  }

  if (status === 422) {
    const raw = data && Array.isArray(data.fields) ? data.fields : [];
    const fields = raw.map((f) => ({
      field: FIELD_MAP[f.field] || f.field,
      message: f.message || null,
      messageKey: null,
    }));
    return { status, fields, messageKey: fields.length ? null : 'analyticsIndicators.err.save' };
  }

  return { status, fields: [], messageKey: 'analyticsIndicators.err.save' };
}

export async function getDictionaries() {
  const net = getNetwork();
  const { data } = await net.get(`${BASE}/dictionaries`);

  // ⛔ ВРЕМЕННЫЙ ЛОГ — удалить после наладки (нужен для разбора единиц в справочнике).
  console.log('[analytics] DICT RAW:', JSON.stringify(data));

  const unitsList = pick(data, 'measure_units') || pick(data, 'units') || [];
  const unitById = {};
  unitsList.forEach((u) => {
    const id = pick(u, 'id');
    if (id != null) unitById[id] = u;
  });

  const indicators = pick(data, 'indicators') || [];
  const periodTypes = pick(data, 'period_types') || [];
  return {
    indicators: indicators.map((it) => mapIndicator(it, unitById)),
    period_types: periodTypes.map(mapPeriodType),
  };
}

export async function getValues(params = {}) {
  const {
    page = 1,
    size = 100,
    search = '',
    indicatorId = null,
    periodType = null,
    dateFrom = null,
    dateTo = null,
  } = params;

  const query = { page, size };
  if (search) query.search = search;
  if (indicatorId) query.indicator_id = indicatorId;
  if (periodType) query.period_type = periodType;
  if (dateFrom) query.date_from = dateFrom;
  if (dateTo) query.date_to = dateTo;

  const net = getNetwork();
  const { data } = await net.get(`${BASE}/values`, { params: query });

  // Список под "items" (Spring-обёртка проекта), плюс запасные ключи.
  const list = pick(data, 'items') || pick(data, 'rows') || pick(data, 'content') || [];
  const total = def(pick(data, 'total'), def(pick(data, 'totalElements'), list.length));

  return { rows: list.map(mapRow), total, page, size };
}

export async function getValue(id) {
  const net = getNetwork();
  const { data } = await net.get(`${BASE}/values/${id}`);
  return data ? mapRow(data) : null;
}

export async function createValue(payload) {
  const body = {
    indicator_id: payload.indicator_id,
    period_type: payload.period_type,
    period_year: payload.period_year,
    period_index: payload.period_type === 'year' ? null : payload.period_index,
    value: payload.value,
  };
  const net = getNetwork();
  const { data } = await net.post(`${BASE}/values`, body);
  return mapRow(data);
}
